import { create } from 'zustand';
import type { OrchestratorStatus } from '@/types';

/**
 * Conversation Summary for sidebar history
 */
export interface ConversationSummary {
  id: string;
  title: string;
  updatedAt: Date;
  status: OrchestratorStatus;
}

/**
 * Chat Store Interface
 */
interface ChatStore {
  // Current conversation state
  hasStarted: boolean;
  status: OrchestratorStatus;

  // Conversation history
  conversations: ConversationSummary[];

  // Actions
  setHasStarted: (value: boolean) => void;
  setStatus: (status: OrchestratorStatus) => void;
  addConversation: (conv: ConversationSummary) => void;
  updateConversation: (id: string, updates: Partial<ConversationSummary>) => void;
  removeConversation: (id: string) => void;

  // Reset function reference (set by ChatPage)
  resetFn: (() => void) | null;
  setResetFn: (fn: (() => void) | null) => void;
}

/**
 * Chat Store - 跨组件状态管理
 *
 * 用于 AppLayout 和 ChatPage 之间共享状态：
 * - 对话状态（用于 Header 显示）
 * - 重置函数（用于 Header 按钮）
 * - 会话历史（用于 Sidebar）
 */
export const useChatStore = create<ChatStore>((set) => ({
  // Initial state
  hasStarted: false,
  status: 'idle',
  conversations: [],
  resetFn: null,

  // Actions
  setHasStarted: (value) => set({ hasStarted: value }),
  setStatus: (status) => set({ status }),

  addConversation: (conv) =>
    set((state) => ({
      conversations: [conv, ...state.conversations].slice(0, 50), // Keep last 50
    })),

  updateConversation: (id, updates) =>
    set((state) => ({
      conversations: state.conversations.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    })),

  removeConversation: (id) =>
    set((state) => ({
      conversations: state.conversations.filter((c) => c.id !== id),
    })),

  setResetFn: (fn) => set({ resetFn: fn }),
}));

/**
 * Load conversations from localStorage
 */
export function loadConversationsFromStorage(): ConversationSummary[] {
  try {
    const data = localStorage.getItem('studyjoyful_conversations');
    if (!data) return [];
    return JSON.parse(data).map((c: ConversationSummary) => ({
      ...c,
      updatedAt: new Date(c.updatedAt),
    }));
  } catch {
    return [];
  }
}

/**
 * Save conversations to localStorage
 */
export function saveConversationsToStorage(conversations: ConversationSummary[]): void {
  try {
    localStorage.setItem('studyjoyful_conversations', JSON.stringify(conversations));
  } catch {
    // Ignore storage errors
  }
}

export default useChatStore;
